/**
 * usePayrollCountry - single source of truth for "what country is this business's Payroll?"
 *
 * Tied to the accounting engine's entity model: one business = one country.
 * Every Payroll page reads from here so we never expose multi-country toggles
 * to a founder who only operates in one country.
 *
 * Backed by GET /api/businesses/me/payroll-context.
 */
import { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { API } from '../App';

/** Current tax year label: calendar "YYYY" (TZ year of income is calendar) or split "YYYY-YY" — GB 6 Apr, ZA 1 Mar, UG 1 Jul. */
export function currentTaxYear(countryCode, today = new Date()) {
  const y = today.getFullYear();
  const startMonth = { GB: 3, ZA: 2, UG: 6 }[countryCode];
  if (startMonth !== undefined) {
    const startDay = countryCode === 'GB' ? 6 : 1;
    const start = (today.getMonth() > startMonth || (today.getMonth() === startMonth && today.getDate() >= startDay)) ? y : y - 1;
    return `${start}-${String(start + 1).slice(2)}`;
  }
  return String(y);
}


const CACHE_TTL_MS = 5 * 60 * 1000;
let cache = null;
let cacheAt = 0;

const DEFAULTS = {
  GB: {
    code: 'GB', name: 'United Kingdom', flag: '🇬🇧',
    currency: 'GBP', currencySymbol: '£',
    regime: 'UK_ENW', taxAuthority: 'HMRC',
    taxYearFormat: currentTaxYear('GB'),
    features: {
      fpsSubmission: true, p60p45: true, cisOverlay: true,
      nhfPension: false, fica: false,
      bankFeedMatch: true, glMapping: true,
      sickLeaveTracking: true, statutoryEligibility: true,
    },
  },
  NG: {
    code: 'NG', name: 'Nigeria', flag: '🇳🇬',
    currency: 'NGN', currencySymbol: '₦',
    regime: 'NG_PAYE', taxAuthority: 'State IRS',
    taxYearFormat: currentTaxYear(),
    features: {
      fpsSubmission: false, p60p45: false, cisOverlay: false,
      nhfPension: true, fica: false,
      bankFeedMatch: true, glMapping: true,
      sickLeaveTracking: false, statutoryEligibility: false,
    },
  },
  US: {
    code: 'US', name: 'United States', flag: '🇺🇸',
    currency: 'USD', currencySymbol: '$',
    regime: 'US_FED_CA', taxAuthority: 'IRS',
    taxYearFormat: currentTaxYear(),
    features: {
      fpsSubmission: false, p60p45: false, cisOverlay: false,
      nhfPension: false, fica: true,
      bankFeedMatch: true, glMapping: true,
      sickLeaveTracking: false, statutoryEligibility: false,
    },
  },
  GH: {
    code: 'GH', name: 'Ghana', flag: '🇬🇭',
    currency: 'GHS', currencySymbol: 'GH₵',
    regime: 'GH_PAYE', taxAuthority: 'GRA',
    taxYearFormat: currentTaxYear(),
    features: {
      fpsSubmission: false, p60p45: false, cisOverlay: false,
      nhfPension: false, fica: false,
      bankFeedMatch: true, glMapping: true,
      sickLeaveTracking: false, statutoryEligibility: false,
      l3Pack: true,
    },
  },
  TZ: {
    code: 'TZ', name: 'Tanzania', flag: '🇹🇿',
    currency: 'TZS', currencySymbol: 'TSh',
    regime: 'TZ_PAYE', taxAuthority: 'TRA',
    taxYearFormat: currentTaxYear(),
    features: {
      fpsSubmission: false, p60p45: false, cisOverlay: false,
      nhfPension: false, fica: false,
      bankFeedMatch: true, glMapping: true,
      sickLeaveTracking: false, statutoryEligibility: false,
      l3Pack: true,
    },
  },
  KE: {
    code: 'KE', name: 'Kenya', flag: '🇰🇪',
    currency: 'KES', currencySymbol: 'KSh',
    regime: 'KE_PAYE', taxAuthority: 'KRA',
    taxYearFormat: currentTaxYear(),
    features: {
      fpsSubmission: false, p60p45: false, cisOverlay: false,
      nhfPension: false, fica: false,
      bankFeedMatch: true, glMapping: true,
      sickLeaveTracking: false, statutoryEligibility: false,
      l3Pack: true,
    },
  },
  ZA: {
    code: 'ZA', name: 'South Africa', flag: '🇿🇦',
    currency: 'ZAR', currencySymbol: 'R',
    regime: 'ZA_PAYE', taxAuthority: 'SARS',
    taxYearFormat: currentTaxYear('ZA'),
    features: {
      fpsSubmission: false, p60p45: false, cisOverlay: false,
      nhfPension: false, fica: false,
      bankFeedMatch: true, glMapping: true,
      sickLeaveTracking: false, statutoryEligibility: false,
      l3Pack: true,
    },
  },
  UG: {
    code: 'UG', name: 'Uganda', flag: '🇺🇬',
    currency: 'UGX', currencySymbol: 'USh',
    regime: 'UG_PAYE', taxAuthority: 'URA',
    taxYearFormat: currentTaxYear('UG'),
    features: {
      fpsSubmission: false, p60p45: false, cisOverlay: false,
      nhfPension: false, fica: false,
      bankFeedMatch: true, glMapping: true,
      sickLeaveTracking: false, statutoryEligibility: false,
      l3Pack: true,
    },
  },
};

function deriveLocal(country) {
  return DEFAULTS[country] || {
    code: country, name: country, flag: '🌍',
    currency: 'USD', currencySymbol: '$',
    regime: 'GENERIC', taxAuthority: 'Tax Authority',
    taxYearFormat: currentTaxYear(),
    features: {
      fpsSubmission: false, p60p45: false, cisOverlay: false,
      nhfPension: false, fica: false,
      bankFeedMatch: true, glMapping: true,
      sickLeaveTracking: false, statutoryEligibility: false,
    },
  };
}

/**
 * React hook returning the payroll context for the active business.
 * Loading state is exposed via .loading.
 */
export function usePayrollCountry() {
  const [ctx, setCtx] = useState(cache);
  const [loading, setLoading] = useState(!cache);

  const fetchCtx = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/business/payroll-context`, { timeout: 8000 });
      // Merge backend response with local defaults so the UI never breaks.
      // Backend returns snake_case (tax_authority); we expose camelCase to the UI.
      const country = (res.data?.code || res.data?.country || 'GB').toUpperCase();
      const local = deriveLocal(country);
      const merged = {
        ...local,
        code: country,
        name: res.data?.name || local.name,
        flag: res.data?.flag || local.flag,
        currency: res.data?.currency || local.currency,
        currencySymbol: res.data?.currency_symbol || local.currencySymbol,
        regime: res.data?.regime || local.regime,
        taxAuthority: res.data?.tax_authority || local.taxAuthority,
        taxYearFormat: res.data?.tax_year_format || local.taxYearFormat,
        defaults: res.data?.defaults || {},
        features: {
          ...local.features,
          ...(res.data?.features
            ? {
                fpsSubmission: res.data.features.fps_submission ?? local.features.fpsSubmission,
                p60p45: res.data.features.p60_p45 ?? local.features.p60p45,
                cisOverlay: res.data.features.cis_overlay ?? local.features.cisOverlay,
                nhfPension: res.data.features.nhf_pension ?? local.features.nhfPension,
                fica: res.data.features.fica ?? local.features.fica,
                bankFeedMatch: res.data.features.bank_feed_match ?? local.features.bankFeedMatch,
                glMapping: res.data.features.gl_mapping ?? local.features.glMapping,
                sickLeaveTracking: res.data.features.sick_leave_tracking ?? local.features.sickLeaveTracking,
                statutoryEligibility: res.data.features.statutory_eligibility ?? local.features.statutoryEligibility,
                governanceBridge: res.data.features.governance_bridge ?? local.features.governanceBridge ?? false,
                l3Pack: res.data.features.l3_pack ?? local.features.l3Pack ?? false,
              }
            : {}),
        },
        payroll_available: res.data.payroll_available !== false,
        rulesSignoff: res.data.rules_signoff || null,
      };
      cache = merged;
      cacheAt = Date.now();
      setCtx(merged);
    } catch (_e) {
      // Fallback to a neutral default if backend lookup fails. We keep the
      // last-known country (when present) instead of forcing GB so a GH/TZ
      // founder never sees UK chrome during a transient API error.
      const fallback = cache || deriveLocal('GB');
      cache = fallback;
      setCtx(fallback);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!cache || (Date.now() - cacheAt) > CACHE_TTL_MS) {
      fetchCtx();
    }
    const onChange = () => fetchCtx();
    window.addEventListener('business-changed', onChange);
    return () => window.removeEventListener('business-changed', onChange);
  }, [fetchCtx]);

  return { ...ctx, loading };
}
